import React, { useState, useEffect, useMemo, useCallback, useReducer } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Table from '@components/table';
import { GetJson } from '@awp/helpers/fetchReact';
import { Views, FORM_MODE, REST_API, EventTypes, PhasedEventTypes } from '@awp/constants';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint, getBreakpoints } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';
import {
  isValueEmpty,
  getUtcToLocalByBreakpoints,
  getLocationPrefix,
  resizePointLocationName,
} from '@awp/helpers/utilsReact';
import { IconCaretDownFill, IconCaretRightFill } from '@awp/components/awpIcons';
import { currentEndDate, currentDisplayDay, currentDisplayDays } from '@stores/avalancheDashboardViewPageSlice';
import { Link } from 'react-router-dom';

const DetailIngestFailureTable = ({ idx, ingestFailedList }) => {
  const [isMedium, setIsMedium] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
  const endDate = useSelector(currentEndDate);
  const displayDays = useSelector(currentDisplayDay);
  const [breakpoints, setBreakpoints] = useState(getBreakpoints());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = debounce(() => {
      setBreakpoints(getBreakpoints());
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });
  useEffect(() => {
    if (isValueEmpty(ingestFailedList) || ingestFailedList.length === 0) return;
    fetchData(); //children function of interest
  }, [ingestFailedList]);

  const fetchData = useCallback(() => {
    setLoading(true);
    let sIds = ingestFailedList.join('-');
    let url = REST_API.AVA_FAILED_DASHBOARD_VIEW + sIds;

    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        if (isValueEmpty(data) || data.length === 0) {
          setData([]);
        } else {
          setData(data);
        }
        setLoading(false);
      });
  }, [ingestFailedList]);

  const cellComponentExp = ({ row }) => (
    // Use Cell to render an expander for each row.
    // We can use the getToggleRowExpandedProps prop-getter
    // to build the expander.
    <span {...row.getToggleRowExpandedProps()}>{row.isExpanded ? <IconCaretDownFill /> : <IconCaretRightFill />}</span>
  );

  const actionMenu = (row) => {
    if (!row || !row.row || !row.row.original) return <></>;
    let id = row.row.original.TraceGuid; //internal candidate id
    let eventType = row.row.original.EventType;
    return (
      <>
        <div>
          {id ? (
            <Link to={Views.Avalanche} state={{ id, mode: FORM_MODE.VIEW, eventType }}>
              <i className="fas fa-eye mx-1 my-1 awp-img-btn" alt="View" />
            </Link>
          ) : (
            ''
          )}
        </div>
      </>
    );
  };

  const getLocation = (row) => {
    if (row.EventType === EventTypes.AvalancheOccurrence) {
      return row.PathLabel || '';
    } else if (row.EventType === EventTypes.AvalancheControl) {
      return row.PathLabel || '';
    } else if (row.EventType === EventTypes.RoadClosure || PhasedEventTypes.Mappings.includes(row.EventType)) {
      if (!isValueEmpty(row.SegmentId)) return row.SegmentLabel;
      if (!isValueEmpty(row.FromPointId) && !isValueEmpty(row.ToPointId))
        return `${row.FromPointLabel} / ${row.ToPointLabel}`;
      if (row.FromPointId) return `${row.FromPointLabel} / - `;
      if (row.ToPointId) return `- / ${row.ToPointLabel}`;
      return '- / -';
    }

    return '';
  };
  const TableColumns = useMemo(
    () => [
      {
        Header: 'Event Date/Time',
        accessor: 'Timestamp',
        Cell: ({ value }) => {
          return getUtcToLocalByBreakpoints(value, breakpoints);
        },
      },
      {
        Header: 'Created',
        accessor: 'UserName',
        show: breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall,
      },
      {
        Header: 'Location',
        accessor: 'location',
        Cell: ({ row }) => {
          const record = row.original;
          const eventType = record.EventType;
          let ret = getLocation(record);
          if (eventType === EventTypes.AvalancheOccurrence || eventType === EventTypes.AvalancheControl) {
            return getLocationPrefix(ret, breakpoints);
          } else {
            if (!isValueEmpty(record.SegmentId)) return getLocationPrefix(ret, breakpoints);
            if (ret) return resizePointLocationName(ret, breakpoints);
          }
          return ret;
        },
      },
      {
        Header: 'Ingestion Error',
        accessor: 'FailureDescription',
        show: breakpoints.isLarge || breakpoints.isMedium || breakpoints.isSmall,
        style: { maxWidth: '30vw' },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [breakpoints],
  );

  return (
    <Table
      id={idx}
      columns={TableColumns}
      data={data}
      fetchData={() => {}}
      loading={loading}
      search={[]}
      filters={[]}
      dataTriggers={[]}
      //hiddenColumns= {["RelativeElevationMillimetres"]}
      //defaultSortBy={["ObservationTypeName"]}
      //renderRowSubComponent={renderRowSubComponent}
      //defaultDesc={true}
      expendCellStyle={{ paddingTop: '0rem', paddingBottom: '0rem' }}
      defaultPageSize={5} //must be one of [5, 10, 25, 50, 100]
    />
  );
};
DetailIngestFailureTable.propTypes = {
  idx: PropTypes.string,
  ingestFailedList: PropTypes.any,
};
export default DetailIngestFailureTable;
